import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type ActivityRecord } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { getActionMeta } from '../lib/activityLabels';
import { Activity, Search, Filter } from 'lucide-react';

function ActivityRow({ record }: { record: ActivityRecord }) {
  const meta = getActionMeta(record.action);
  const [colorText, colorBg] = meta.color.split(' ');
  const details = record.details ?? {};
  const detailStr = Object.entries(details)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
    .join(' · ');

  return (
    <div className="flex items-start gap-3 rounded-lg bg-white/[0.03] px-3 py-2.5">
      <span className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${colorText} ${colorBg}`}>
        {meta.label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-xs">
          {record.actorId && (
            // Lead with the human name, but keep the raw snowflake on screen —
            // admins cross-reference it and paste it into /admin/user-lookup.
            // actorName is absent for unresolvable/POPIA-purged actors, so the
            // id stays the primary label in that case rather than showing blank.
            <Link
              to={`/admin/user-lookup?id=${encodeURIComponent(record.actorId)}`}
              title={record.actorName ? `${record.actorName} · ${record.actorId}` : record.actorId}
              className="flex min-w-0 items-baseline gap-1.5 text-gray-400 hover:text-ink-primary transition-colors"
            >
              <span className={cn('truncate', record.actorName ? 'text-gray-300' : 'font-mono')}>
                {record.actorName ?? record.actorId}
              </span>
              {record.actorName && (
                <span className="shrink-0 font-mono text-[10px] text-gray-600">
                  {record.actorId}
                </span>
              )}
            </Link>
          )}
          <span className="text-gray-600">→</span>
          <span className="font-mono text-gray-400 truncate">{record.target}</span>
        </div>
        {detailStr && (
          <p className="mt-0.5 text-[10px] text-gray-600 truncate">{detailStr}</p>
        )}
      </div>
      <span className="shrink-0 text-[10px] text-gray-600">
        {new Date(record.createdAt).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </span>
    </div>
  );
}

export function ActivityLog() {
  const [actorFilter, setActorFilter] = useState('');
  const [activeActor, setActiveActor] = useState('');
  const [source, setSource] = useState<'all' | 'portal' | 'discord' | 'system'>('all');

  // Use actor-specific query if filtered, otherwise timeline
  const { data: timelineData, isLoading: tlLoading } = useQuery({
    queryKey: ['admin', 'activity-timeline'],
    queryFn: () => api.admin.activityTimeline(100),
    enabled: !activeActor,
  });

  const { data: actorData, isLoading: actorLoading } = useQuery({
    queryKey: ['admin', 'activity-actor', activeActor],
    queryFn: () => api.admin.activityByActor(activeActor, 100),
    enabled: !!activeActor,
  });

  const isLoading = activeActor ? actorLoading : tlLoading;
  const rawRecords = activeActor ? actorData : timelineData;

  // Apply source filter
  const records = (rawRecords ?? []).filter(r => source === 'all' || r.source === source);

  const handleActorSearch = () => {
    const trimmed = actorFilter.trim();
    setActiveActor(trimmed);
  };

  const clearActor = () => {
    setActorFilter('');
    setActiveActor('');
  };

  return (
    <div>
      <div className={cn('mb-8 flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <Activity className="h-7 w-7 text-accent-500" />
        <div>
          <h1 className="text-2xl font-bold text-white">Activity Log</h1>
          <p className="text-xs text-gray-500">Every tracked action across the platform</p>
        </div>
      </div>

      {/* Filters */}
      <div className={SETTLE} style={settleDelay(1)}>
        <Card className="mb-6">
          <div className="flex flex-wrap gap-3">
            {/* Actor search */}
            <div className="flex gap-2 flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Filter by Discord ID..."
                value={actorFilter}
                onChange={e => setActorFilter(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleActorSearch()}
                className="input flex-1 font-mono"
              />
              <button
                onClick={handleActorSearch}
                className={cn('btn-primary px-3', MORPH)}
              >
                <Search className="h-3.5 w-3.5" />
              </button>
              {activeActor && (
                <button onClick={clearActor} className="btn-ghost text-xs">
                  Clear
                </button>
              )}
            </div>

            {/* Source filter */}
            <div className="flex items-center gap-1">
              <Filter className="h-3.5 w-3.5 text-gray-500" />
              {(['all', 'portal', 'discord', 'system'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setSource(s)}
                  className={cn('rounded-md px-2 py-1 text-xs transition-colors', source === s ? 'bg-white/[0.07] text-accent-500' : 'text-gray-500 hover:text-gray-300')}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          {activeActor && (
            <p className="mt-2 text-xs text-ink-secondary">Showing activity for: {activeActor}</p>
          )}
        </Card>
      </div>

      {/* Results */}
      {isLoading ? (
        <PageSpinner />
      ) : records.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">No activity records yet. Actions will appear here once the audit log starts capturing data.</p>
        </Card>
      ) : (
        <div className={cn('space-y-1.5', SETTLE)} style={settleDelay(2)}>
          {records.map((r) => (
            <ActivityRow key={r.sk} record={r} />
          ))}
          {records.length >= 100 && (
            <p className="mt-4 text-center text-xs text-gray-600">Showing latest 100 records</p>
          )}
        </div>
      )}
    </div>
  );
}
